import Link from 'next/link';
import {
  BlogCategoryDocument,
  BlogDocumentData,
} from '../../../prismicio-types';
import { asText } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { AuthorCard } from '../blog/AuthorCard';
import { getBlogPost } from '@/hooks';

type Props = BlogDocumentData & {
  cardType:
    | 'simple'
    | 'main'
    | 'horizontal'
    | 'featured'
    | 'with_author'
    | 'without_author'
    | 'new-main'
    | 'new-featured'
    | 'new-simple';
  uid: string;
  containerClass?: string;
  date?: string;
};

export default async function BlogCard(props: Readonly<Props>) {
  const category =
    (props.category as unknown as BlogCategoryDocument) ||
    props.all_categories[0];
  const { author } = await getBlogPost(props.uid);

  const renderDateAndDuration = () => {
    const parsedDate = new Date(props.date!).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const parsedDuration = props.read_duration
      ? `• ${props.read_duration} min read`
      : '';
    return (
      <div>
        {parsedDate} {parsedDuration}
      </div>
    );
  };

  const getCardHeight = () => {
    switch (props.cardType) {
      case 'main':
        return 'h-[450px]';
      case 'horizontal':
        return 'h-[250px] min-w-[40%]';
      case 'featured':
        return 'h-auto';
      case 'new-main':
        return 'h-auto';
      case 'new-featured':
        return 'h-full max-w-[120px]';
      default:
        return 'h-fit';
    }
  };

  const getTitleStyle = () => {
    if (props.cardType === 'main') {
      return 'text-[32px] font-bold';
    }
    if (props.cardType === 'new-simple') {
      return 'text-lg font-bold';
    }
    if (props.cardType === 'new-featured') {
      // 30% upscale from previous text-lg (~18px) => ~23.4px
      return 'text-[23px] font-semibold';
    }
    return 'text-2xl font-bold';
  };

  const getImageBorder = () => {
    if (props.cardType === 'featured' || props.cardType === 'new-simple') {
      return 'rounded-t-[20px]';
    } else if (props.cardType === 'new-featured') {
      return 'rounded-[10px]';
    }
    return 'rounded-[20px]';
  };

  if (props.cardType === 'new-main') {
    return (
      <div
        className="flex flex-col justify-between items-start w-full md:w-[890px] md:h-[890px] md:max-w-none overflow-hidden text-start rounded-[16px] p-8 backdrop-blur-[10px] bg-gradient-to-b from-[#00000066] to-[#00000033] shadow-[0px_4px_64px_rgba(0,0,0,0.08)]"
        style={{ WebkitBackdropFilter: 'blur(10px)' }}
      >
        <div className="w-full">
          <Link href={`/blog/${props.uid}`} className="w-full block">
            <PrismicNextImage
              field={props.image}
              className={`w-full self-stretch aspect-square h-[560px] object-cover ${getImageBorder()}`}
              priority
            />
          </Link>
        </div>
        <div className="flex flex-col w-full flex-1 mt-6">
          {category && category?.data?.name && (
            <Link
              href={`/blog?category=${category?.uid}`}
              className="text-xs font-medium uppercase text-[#05F273] mb-3"
            >
              {category?.data?.name}
            </Link>
          )}
          <Link href={`/blog/${props.uid}`} className="block">
            <div className="text-[45px] font-bold leading-[1.15] text-white">
              {asText(props.card_title)}
            </div>
          </Link>
        </div>
        <div className="flex items-center gap-6 pt-6 w-full">
          <AuthorCard
            variation="date_and_time"
            date={`${props.date}`}
            duration={props.read_duration}
            name={author.data?.name ?? 'Devsu'}
            avatar={author.data?.avatar}
            authorUid={author.uid}
            avatarClassName="w-[60px] h-[60px] rounded-[12px] object-cover"
          />
        </div>
      </div>
    );
  }

  if (props.cardType === 'new-featured') {
    return (
      <div
        className="flex flex-col w-full rounded-[16px] p-[26px] items-start overflow-hidden text-start min-h-[269px]"
        style={{
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.25))',
        }}
      >
        <div className="w-full flex flex-col flex-1">
          <Link href={`/blog/${props.uid}`} className="block">
            <div
              className={`${getTitleStyle()} leading-snug overflow-hidden line-clamp-3`}
            >
              {asText(props.card_title)}
            </div>
          </Link>
          <div className="flex-1 flex flex-col justify-center">
            <div className="text-[21px] mt-3 mb-3">
              {renderDateAndDuration()}
            </div>
          </div>
          {category && category?.data?.name && (
            <Link
              href={`/blog?category=${category?.uid}`}
              className="pt-1 w-fit text-[16px] tracking-wide text-[#05F273] uppercase"
            >
              {category?.data?.name}
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (props.cardType === 'new-simple') {
    return (
      <div
        className="flex flex-col rounded-[20px] overflow-hidden text-start text-white h-full"
        style={{
          background:
            'linear-gradient(to right, rgba(255,255,255,0.15), rgba(3,191,158,0.15), rgba(255,255,255,0.15))',
        }}
      >
        {props.image?.url && (
          <div className="relative h-48">
            <PrismicNextImage
              field={props.image}
              width={400}
              height={250}
              className={`h-full w-full object-cover ${getImageBorder()}`}
              priority
            />
            {/* Abstract overlay - simplified for Tailwind */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            <div className="absolute top-4 right-4 text-white"></div>
          </div>
        )}
        <div className="p-6 flex flex-col justify-between h-full">
          <div>
            <Link href={`/blog/${props.uid}`}>
              <h3 className="text-xl font-bold text-white mb-2">
                {asText(props.card_title)}
              </h3>
            </Link>
          </div>
          <div className="flex flex-col gap-4">
            <p className="text-green-accent text-sm font-semibold uppercase">
              {category && category?.data?.name && (
                <Link
                  href={`/blog?category=${category.uid}`}
                  className="text-xs text-[#05F273] font-semibold tracking-wider no-underline"
                >
                  {category.data.name.toUpperCase()}
                </Link>
              )}
            </p>
            <div className="flex items-center space-x-3">
              <AuthorCard
                variation="date_and_time"
                date={`${props.date}`}
                duration={props.read_duration}
                name={author.data?.name ?? 'Devsu'}
                avatar={author.data?.avatar}
                authorUid={author.uid}
                avatarClassName="rounded-[6px] object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={props.containerClass}>
      <div className={`${getCardHeight()} flex justify-center overflow-hidden`}>
        <Link href={`/blog/${props.uid}`}>
          <PrismicNextImage
            field={props.image}
            className={`self-center ${props.cardType === 'main' ? 'h-full w-auto' : 'h-auto w-full'} overflow-hidden ${getImageBorder()}`}
            priority
          />
        </Link>
      </div>
      <div
        className={`flex flex-col ${props.cardType === 'featured' && 'pb-6 px-6 pt-3'}`}
      >
        <div
          className={`${props.cardType !== 'horizontal' ? 'py-6' : 'pb-1'} flex flex-col gap-4`}
        >
          <div className="flex flex-row text-sm items-center gap-4">
            {category && category?.data?.name && (
              <Link
                href={`/blog?category=${category?.uid}`}
                className="px-2 py-1 text-primary-shade-green bg-primary-light-grey font-semibold"
              >
                {category?.data?.name}
              </Link>
            )}
            {props.read_duration && (
              <div className="font-semibold">{`${props.read_duration} min read`}</div>
            )}
          </div>
          <Link href={`/blog/${props.uid}`}>
            <div className={`${getTitleStyle()} overflow-hidden line-clamp-2`}>
              {asText(props.card_title)}
            </div>
          </Link>
          {props.meta_description && (
            <div className="overflow-hidden line-clamp-3">
              {props.meta_description}
            </div>
          )}
        </div>
        <div className="pb-2">
          {props.cardType !== 'without_author' && (
            <AuthorCard
              variation="date_and_time"
              date={`${props.date}`}
              duration={props.read_duration}
              name={author.data?.name ?? 'Devsu'}
              avatar={author.data?.avatar}
              authorUid={author.uid}
            />
          )}
        </div>
      </div>
    </div>
  );
}
